// ─── Autonomous Figure / Table Completion Engine ──────────────────────────────
// Phase 7 of the Autonomous Generation Phase.
//
// Detects chapters that reference regression, correlation, or descriptive
// analysis results but have no corresponding chart figure inline.
// Auto-generates SVG charts and injects them as self-contained
// <div data-thesis-figure data-figure-type="chart-svg"> tokens — the same
// format used by the mermaid/table pipeline and rendered by ThesisFigureExtension.
//
// The token is FULLY SELF-CONTAINED: SVG is URL-encoded in data-figure-code.
// No separate DB lookup required. Figures survive refresh, regeneration, export.

import { supabase } from '../../lib/supabase'
import { buildSVGFigureToken, injectFigureAfterParagraph } from '../figurePipeline'
import {
  coefficientPlotSVG,
  correlationHeatmapSVG,
  histogramSVG,
} from '../research/chartEngine'

// ── Detection patterns ────────────────────────────────────────────────────────

const REGRESSION_CUES = [
  /regression\s+(?:coefficient|result|model|analysis|output)/i,
  /OLS\s+(?:result|output|regression|analysis)/i,
  /coefficient\s+estimate/i,
  /standardised?\s+(?:beta|coefficient)/i,
  /\bR²\b|\bR-squared\b|\badj(?:usted)?\s+R/i,
  /significant\s+predictor/i,
]

const CORRELATION_CUES = [
  /correlation\s+matrix/i,
  /bivariate\s+correlation/i,
  /inter-?variable\s+correlation/i,
  /Pearson\s+correlation/i,
  /correlation\s+(?:coefficient|analysis|table)/i,
]

const DESCRIPTIVE_CUES = [
  /descriptive\s+statistic/i,
  /mean\s+(?:score|value|response)/i,
  /mean\s+and\s+standard\s+deviation/i,
  /summary\s+statistic/i,
]

// ── Helpers ───────────────────────────────────────────────────────────────────

function stripHtml(html) {
  return (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ')
}

function chapterType(title = '') {
  if (/result|finding|analys/i.test(title)) return 'results'
  if (/discussion|interpret/i.test(title)) return 'discussion'
  if (/method/i.test(title)) return 'methodology'
  if (/literature|review/i.test(title)) return 'literature'
  if (/conclusion/i.test(title)) return 'conclusion'
  return 'other'
}

function hasSVGFigure(html) {
  return /data-figure-type="chart-svg"/i.test(html || '')
}

function hasAnyFigureToken(html) {
  return /data-thesis-figure="true"/i.test(html || '')
}

// ── Descriptive bar chart ─────────────────────────────────────────────────────

function descriptiveBarSVG({ variables, means, stdDevs, title }) {
  const n = variables.length
  if (n === 0) return null

  const W = 560
  const barW = Math.min(60, Math.floor(460 / n))
  const gapW = Math.floor(barW * 0.4)
  const marginLeft = 60, marginTop = 50, marginBottom = 80
  const H = 380
  const iH = H - marginTop - marginBottom
  const iW = n * (barW + gapW)

  const maxVal = Math.max(...means) * 1.15 || 5

  function scaleY(v) { return iH - (v / maxVal) * iH }

  function escapeXml(str) {
    return String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  }

  let bars = ''
  variables.forEach((v, i) => {
    const x = i * (barW + gapW)
    const barH = (means[i] / maxVal) * iH
    const y = iH - barH
    bars += `<rect x="${x}" y="${y}" width="${barW}" height="${barH}" fill="#2563eb" opacity="0.8" rx="2"/>`
    bars += `<text x="${x + barW / 2}" y="${y - 5}" text-anchor="middle" font-size="9" fill="#334155">${Number(means[i]).toFixed(2)}</text>`
    bars += `<text x="${x + barW / 2}" y="${iH + 16}" text-anchor="end" font-size="9" fill="#475569" transform="rotate(-35,${x + barW / 2},${iH + 16})">${escapeXml(v.replace(/_/g, ' '))}</text>`
    if (stdDevs?.[i] != null) {
      const sdPx = (stdDevs[i] / maxVal) * iH
      const midX = x + barW / 2
      bars += `<line x1="${midX}" y1="${y - sdPx}" x2="${midX}" y2="${y + Math.min(sdPx, barH * 0.5)}" stroke="#64748b" stroke-width="1.5"/>`
      bars += `<line x1="${midX - 5}" y1="${y - sdPx}" x2="${midX + 5}" y2="${y - sdPx}" stroke="#64748b" stroke-width="1.5"/>`
    }
  })

  let yAxis = `<line x1="0" y1="0" x2="0" y2="${iH}" stroke="#334155" stroke-width="1.5"/>`
  for (let t = 0; t <= Math.ceil(maxVal); t++) {
    if (t > maxVal) break
    const y = scaleY(t)
    yAxis += `<line x1="-4" y1="${y}" x2="0" y2="${y}" stroke="#334155" stroke-width="1"/>`
    yAxis += `<text x="-8" y="${y + 4}" text-anchor="end" font-size="9" fill="#475569">${t}</text>`
  }
  yAxis += `<text x="${-iH / 2}" y="-42" text-anchor="middle" font-size="11" fill="#334155" transform="rotate(-90)">Mean Score</text>`

  const inner = `<g transform="translate(${marginLeft},${marginTop})">${bars}${yAxis}</g>`
  const titleEl = `<text x="${W / 2}" y="22" text-anchor="middle" font-size="13" font-weight="bold" fill="#0f172a">${escapeXml(title || 'Descriptive Statistics — Variable Means')}</text>`

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" font-family="'Times New Roman', Georgia, serif">
  <rect width="${W}" height="${H}" fill="white"/>
  ${titleEl}
  ${inner}
  <text x="${W - 10}" y="${H - 5}" text-anchor="end" font-size="8" fill="#94a3b8">Error bars = ±1 SD</text>
</svg>`
}

// ── SVG generation ────────────────────────────────────────────────────────────

function generateCoefPlotSVG(ols, thesis) {
  if (!ols?.coefficients) return null
  return coefficientPlotSVG({
    coefficients: ols.coefficients,
    dvLabel: ols.dv || thesis?.title || 'Outcome Variable',
    title: `Regression Coefficient Estimates (${ols.dv || 'Outcome Variable'})`,
  })
}

function generateCorrelationHeatmapSVG(corr, variables) {
  if (!corr?.matrix) return null
  const vars = variables?.length
    ? variables.map(v => (typeof v === 'string' ? v : v.name)).filter(Boolean)
    : Object.keys(corr.matrix)
  if (vars.length < 2) return null
  return correlationHeatmapSVG({
    matrix: corr.matrix,
    pvalues: corr.pvalues || {},
    variables: vars,
    title: 'Correlation Matrix of Study Variables',
  })
}

function generateDescriptiveBarSVG(desc, variables) {
  if (!desc) return null
  const varNames = variables?.length
    ? variables.map(v => (typeof v === 'string' ? v : v.name)).filter(v => desc[v]?.mean != null)
    : Object.keys(desc).filter(k => desc[k]?.mean != null)
  if (varNames.length === 0) return null
  return descriptiveBarSVG({
    variables: varNames,
    means: varNames.map(v => desc[v]?.mean || 0),
    stdDevs: varNames.map(v => desc[v]?.std || null),
    title: 'Descriptive Statistics — Variable Means',
  })
}

// ── Main pipeline ─────────────────────────────────────────────────────────────

/**
 * Scan chapters for missing SVG chart figures, generate them, and inject
 * as self-contained <div data-thesis-figure data-figure-type="chart-svg"> tokens.
 *
 * @param {string} thesisId
 * @param {Array<{ id, title, content, chapter_number }>} chapters
 * @param {{
 *   researchResults: { ols, correlation, descriptive, cronbach },
 *   variables: Array,
 *   thesis: object,
 * }} context
 *
 * @returns {{ updatedChapters: Array, generated: Array, skipped: string[] }}
 */
export function runAutonomousFigureCompletion(thesisId, chapters, context = {}) {
  const { researchResults = {}, variables = [], thesis = {} } = context

  const ols  = researchResults.ols  || null
  const corr = researchResults.correlation || null
  const desc = researchResults.descriptive || null

  const generated = []
  const skipped   = []
  const updatedChapters = chapters.map(c => ({ ...c }))

  // Track per-chapter what we've already injected (avoid duplicates within a run)
  const injected = new Set()

  for (let i = 0; i < updatedChapters.length; i++) {
    const ch    = updatedChapters[i]
    const plain = stripHtml(ch.content)
    const type  = chapterType(ch.title)

    // ── Coefficient plot ────────────────────────────────────────────────────
    const needsCoef = (
      ols &&
      REGRESSION_CUES.some(re => re.test(plain)) &&
      !injected.has(`${ch.id}:coef`) &&
      !hasSVGFigure(ch.content) &&          // only inject once per chapter
      (type === 'results' || type === 'discussion' || type === 'other')
    )

    if (needsCoef) {
      try {
        const svg = generateCoefPlotSVG(ols, thesis)
        if (svg) {
          const token = `fig_${(ch.chapter_number || i + 1)}_coef`
          const figHTML = buildSVGFigureToken({
            token,
            svgCode: svg,
            title: `Figure: Regression Coefficient Estimates — ${ols.dv || 'Outcome Variable'}`,
            caption: `Filled squares indicate statistical significance (p < .05). Error bars represent 95% confidence intervals. N = ${ols.n || 'n/a'}.`,
          })
          updatedChapters[i].content = injectFigureAfterParagraph(
            updatedChapters[i].content, figHTML, REGRESSION_CUES
          )
          injected.add(`${ch.id}:coef`)
          generated.push({ figureType: 'coefficient_plot', chapterId: ch.id, chapterTitle: ch.title, token })
        }
      } catch (err) {
        skipped.push(`coefficient_plot in "${ch.title}": ${err.message}`)
      }
    }

    // ── Correlation heatmap ─────────────────────────────────────────────────
    const needsHeatmap = (
      corr &&
      CORRELATION_CUES.some(re => re.test(plain)) &&
      !injected.has(`${ch.id}:corr`) &&
      (type === 'results' || type === 'methodology' || type === 'other')
    )

    if (needsHeatmap) {
      try {
        const varList = variables.map(v => (typeof v === 'string' ? v : v.name)).filter(Boolean)
        const svg = generateCorrelationHeatmapSVG(corr, varList)
        if (svg) {
          const token = `fig_${(ch.chapter_number || i + 1)}_corr`
          const figHTML = buildSVGFigureToken({
            token,
            svgCode: svg,
            title: 'Figure: Pearson Correlation Matrix',
            caption: 'Values indicate Pearson r coefficients. Bold values and asterisks (*) indicate p < .05. Blue = positive correlation; Red = negative correlation.',
          })
          updatedChapters[i].content = injectFigureAfterParagraph(
            updatedChapters[i].content, figHTML, CORRELATION_CUES
          )
          injected.add(`${ch.id}:corr`)
          generated.push({ figureType: 'correlation_heatmap', chapterId: ch.id, chapterTitle: ch.title, token })
        }
      } catch (err) {
        skipped.push(`correlation_heatmap in "${ch.title}": ${err.message}`)
      }
    }

    // ── Descriptive bar chart ───────────────────────────────────────────────
    const needsDesc = (
      desc &&
      DESCRIPTIVE_CUES.some(re => re.test(plain)) &&
      !injected.has(`${ch.id}:desc`) &&
      !injected.has(`${ch.id}:coef`) &&     // don't stack charts in same chapter
      type === 'results'
    )

    if (needsDesc) {
      try {
        const varList = variables.map(v => (typeof v === 'string' ? v : v.name)).filter(Boolean)
        const svg = generateDescriptiveBarSVG(desc, varList)
        if (svg) {
          const token = `fig_${(ch.chapter_number || i + 1)}_desc`
          const figHTML = buildSVGFigureToken({
            token,
            svgCode: svg,
            title: 'Figure: Descriptive Statistics — Variable Means',
            caption: 'Mean scores for study variables. Error bars represent ±1 standard deviation. Scale: 1–5 Likert.',
          })
          updatedChapters[i].content = injectFigureAfterParagraph(
            updatedChapters[i].content, figHTML, DESCRIPTIVE_CUES
          )
          injected.add(`${ch.id}:desc`)
          generated.push({ figureType: 'descriptive_bar', chapterId: ch.id, chapterTitle: ch.title, token })
        }
      } catch (err) {
        skipped.push(`descriptive_bar in "${ch.title}": ${err.message}`)
      }
    }
  }

  return { updatedChapters, generated, skipped }
}

/**
 * Run figure completion then persist changed chapters to Supabase.
 */
export async function runAndPersistFigureCompletion(thesisId, chapters, context = {}) {
  const original = chapters.map(c => ({ ...c }))
  const { updatedChapters, generated, skipped } = runAutonomousFigureCompletion(thesisId, chapters, context)

  const errors = []
  let saved = 0

  for (let i = 0; i < updatedChapters.length; i++) {
    if (updatedChapters[i].content === original[i]?.content) continue
    const { error } = await supabase
      .from('chapters')
      .update({ content: updatedChapters[i].content, updated_at: new Date().toISOString() })
      .eq('id', updatedChapters[i].id)
    if (error) {
      errors.push(`Failed to save "${updatedChapters[i].title}": ${error.message}`)
    } else {
      saved++
    }
  }

  const report = generated.length === 0 && skipped.length === 0
    ? 'No figure gaps detected — all chapters already have visual anchors.'
    : [
        generated.length > 0 ? `Generated ${generated.length} figure(s) in: ${generated.map(g => `"${g.chapterTitle}"`).join(', ')}.` : '',
        skipped.length > 0 ? `${skipped.length} skipped: ${skipped.join('; ')}.` : '',
      ].filter(Boolean).join(' ')

  return { generated, skipped, saved, errors, updatedChapters, report }
}

/**
 * Quick scan — returns whether any chapters need auto-generated figures.
 */
export function scanForFigureGaps(chapters, researchResults = {}) {
  const details = []
  let count = 0

  for (const ch of chapters) {
    const plain = stripHtml(ch.content)

    if (researchResults.ols && REGRESSION_CUES.some(re => re.test(plain)) && !hasSVGFigure(ch.content)) {
      details.push(`"${ch.title}" discusses regression but has no coefficient chart`)
      count++
    }
    if (researchResults.correlation && CORRELATION_CUES.some(re => re.test(plain)) && !hasAnyFigureToken(ch.content)) {
      details.push(`"${ch.title}" discusses correlations but has no heatmap`)
      count++
    }
    if (researchResults.descriptive && DESCRIPTIVE_CUES.some(re => re.test(plain)) && !hasAnyFigureToken(ch.content) && chapterType(ch.title) === 'results') {
      details.push(`"${ch.title}" discusses descriptive stats but has no bar chart`)
      count++
    }
  }

  return { needsFigures: count > 0, count, details }
}
